import React, { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { track } from '../lib/ga';

const PaymentSuccess: React.FC = () => {
  const { isLoaded, isSignedIn } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  const type = searchParams.get('type') || 'eulogy';
  const name = searchParams.get('name') || 'your loved one';
  const relationship = searchParams.get('relationship') || '';

  useEffect(() => {
    document.title = 'Cyrano - Payment Successful';
    // Funnel analytics: the conversion event. Fired once per success-page view.
    track('purchase', { type });
    window.scrollTo(0, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer - redirect after 5 seconds
  useEffect(() => {
    if (countdown <= 0) {
      // Redirect to conversation with autoAdvance parameter
      navigate(`/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&relationship=${encodeURIComponent(relationship)}&autoAdvance=true`);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, navigate, type, name]);

  // Show loading spinner while Clerk loads
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-space-cadet"></div>
      </div>
    );
  }

  // Redirect to sign in if not authenticated
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <div className="min-h-screen bg-warm-background flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
        {/* Success Icon */}
        <div className="mb-6 flex justify-center">
          <img
            src="/Assets/200-handdrawn-icons-by-heloholo/png/64/check_ok64.png"
            alt="Success"
            className="w-20 h-20"
          />
        </div>

        {/* Main Heading */}
        <h1 className="font-serif text-3xl md:text-4xl font-semibold text-space-cadet mb-4">
          Welcome to Cyrano
        </h1>

        {/* Success Message */}
        <p className="text-space-cadet text-xl font-medium mb-6">
          Your payment was successful. Let's continue creating.
        </p>

        {/* Progress Message */}
        <p className="text-cool-gray text-lg mb-8 leading-relaxed">
          You're creating a meaningful {type === 'eulogy' ? 'eulogy' : 'tribute'} for {name}. Your progress has been saved—let's pick up right where you left off.
        </p>

        {/* Features List */}
        <div className="text-left max-w-md mx-auto mb-8 bg-warm-background rounded-lg p-6">
          <p className="text-space-cadet font-semibold mb-4 text-center">You now have full access to:</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-space-cadet-primary mt-1">✓</span>
              <span className="text-cool-gray">All guided questions and expert prompts</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-space-cadet-primary mt-1">✓</span>
              <span className="text-cool-gray">Full editing tools and collaboration features</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-space-cadet-primary mt-1">✓</span>
              <span className="text-cool-gray">Ongoing access with all export options</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <button
            onClick={() => navigate(`/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&relationship=${encodeURIComponent(relationship)}&autoAdvance=true`)}
            className="bg-space-cadet-secondary text-white px-8 py-3 rounded-lg font-medium hover:bg-space-cadet-primary transition-colors shadow-lg"
          >
            Continue Creating Your {type === 'eulogy' ? 'Eulogy' : 'Tribute'}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-transparent border-2 border-space-cadet-secondary text-space-cadet-secondary px-8 py-3 rounded-lg font-medium hover:bg-space-cadet-secondary hover:text-white transition-colors"
          >
            View your account
          </button>
        </div>

        {/* Footer Info */}
        <div className="border-t border-cool-gray/20 pt-6 mt-6">
          <p className="text-cool-gray text-sm mb-2">
            A receipt has been sent to your email.
          </p>
          <p className="text-cool-gray text-sm">
            Need help? <a href="mailto:support@cyrano.cloud" className="text-space-cadet-primary hover:underline">Contact support</a>
          </p>
        </div>

        {/* Countdown Indicator */}
        <div className="mt-6 text-cool-gray text-sm">
          Redirecting automatically in {countdown} second{countdown !== 1 ? 's' : ''}...
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
